'use client';

import { useAppDispatch } from '@/store/hooks';
import { Brand } from '@/types/Brand';
import getEnv from '@/utils/env';
import { globalLogout } from '@/utils/LogoutUtils';

import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from 'react';

interface InactivityContextType {
  resetTimer: () => void;
}

const InactivityContext = createContext<InactivityContextType | undefined>(
  undefined
);

interface InactivityProviderProps {
  excludePaths?: string[];
  children: ReactNode;
  brand: Brand;
}

export const InactivityProvider: React.FC<InactivityProviderProps> = ({
  excludePaths = [],
  children,
  brand,
}) => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const timeout = parseInt(getEnv().timeout);
  const dispatch = useAppDispatch();

  const handleTimeout = () => {
    globalLogout({ brand, dispatch });
  };

  const resetTimer = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(handleTimeout, timeout);
  }, [timeout]);

  useEffect(() => {
    if (
      excludePaths.some((path) => window.location.pathname.includes('/' + path))
    ) {
      return;
    }
    const handleActivity = () => {
      resetTimer();
    };

    const events = [
      'mousemove',
      'keydown',
      'wheel',
      'DOMMouseScroll',
      'mousewheel',
      'mousedown',
      'touchstart',
      'touchmove',
      'MSPointerDown',
      'MSPointerMove',
      'visibilitychange',
    ] as const;
    events.forEach((event) => window.addEventListener(event, handleActivity));

    resetTimer();

    return () => {
      events.forEach((event) =>
        window.removeEventListener(event, handleActivity)
      );
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [resetTimer, excludePaths]);

  return (
    <InactivityContext.Provider value={{ resetTimer }}>
      {children}
    </InactivityContext.Provider>
  );
};

export const useInactivity = (): InactivityContextType => {
  const context = useContext(InactivityContext);
  if (!context) {
    throw Error('Not inside context');
  }
  return context;
};
